import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Create a glossy, modern theme with vibrant colors and glass effects
let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5E17EB', // Vibrant purple for main accent
      light: '#8352FF',
      dark: '#4500D4',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#22CAAC', // Teal for secondary accent
      light: '#4DDFCA',
      dark: '#00B894',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F0F2F5',
      paper: 'rgba(255, 255, 255, 0.95)',
    },
    text: {
      primary: '#15192E',
      secondary: '#4A5568',
      disabled: '#A0AEC0',
    },
    error: {
      main: '#FF4957',
    },
    warning: {
      main: '#FF9500',
    },
    info: {
      main: '#00A3FF',
    },
    success: {
      main: '#00D174',
    },
    divider: 'rgba(0, 0, 0, 0.06)',
    action: {
      active: 'rgba(94, 23, 235, 0.54)',
      hover: 'rgba(94, 23, 235, 0.04)',
      selected: 'rgba(94, 23, 235, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
      fontWeight: 700,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontSize: '2rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
      fontWeight: 600,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
      fontWeight: 600,
      letterSpacing: '0em',
    },
    h4: {
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
      fontWeight: 600,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
      fontWeight: 500,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: '1.125rem',
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
      fontWeight: 500,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.9rem',
      },
      fontWeight: 400,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      '@media (max-width:600px)': {
        fontSize: '0.8rem',
      },
      fontWeight: 500,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.9rem',
      },
      fontWeight: 400,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      '@media (max-width:600px)': {
        fontSize: '0.8rem',
      },
      fontWeight: 400,
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: '0.875rem',
      '@media (max-width:600px)': {
        fontSize: '0.8rem',
      },
      fontWeight: 500,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
    caption: {
      fontWeight: 400,
      letterSpacing: 0,
    },
    overline: {
      fontWeight: 500,
      letterSpacing: '0.05em',
    },
  },
  shape: {
    borderRadius: 16, // Increased border radius for modern feel
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(30, 30, 60, 0.06), 0px 1px 3px rgba(30, 30, 60, 0.1)',
    '0px 2px 4px rgba(30, 30, 60, 0.06), 0px 2px 5px rgba(30, 30, 60, 0.1)',
    '0px 3px 6px rgba(30, 30, 60, 0.06), 0px 4px 8px rgba(30, 30, 60, 0.1)',
    '0px 4px 8px rgba(30, 30, 60, 0.06), 0px 8px 16px rgba(30, 30, 60, 0.1)',
    '0px 5px 10px rgba(30, 30, 60, 0.06), 0px 12px 20px rgba(30, 30, 60, 0.1)',
    '0px 6px 14px rgba(30, 30, 60, 0.06), 0px 18px 25px rgba(30, 30, 60, 0.1)',
    '0px 7px 16px rgba(30, 30, 60, 0.06), 0px 24px 30px rgba(30, 30, 60, 0.1)',
    '0px 8px 18px rgba(30, 30, 60, 0.06), 0px 28px 35px rgba(30, 30, 60, 0.1)',
    '0px 9px 20px rgba(30, 30, 60, 0.06), 0px 32px 40px rgba(30, 30, 60, 0.1)',
    '0px 10px 22px rgba(30, 30, 60, 0.06), 0px 36px 45px rgba(30, 30, 60, 0.1)',
    '0px 11px 24px rgba(30, 30, 60, 0.06), 0px 40px 50px rgba(30, 30, 60, 0.1)',
    '0px 12px 26px rgba(30, 30, 60, 0.06), 0px 44px 55px rgba(30, 30, 60, 0.1)',
    '0px 13px 28px rgba(30, 30, 60, 0.06), 0px 48px 60px rgba(30, 30, 60, 0.1)',
    '0px 14px 30px rgba(30, 30, 60, 0.06), 0px 52px 65px rgba(30, 30, 60, 0.1)',
    '0px 15px 32px rgba(30, 30, 60, 0.06), 0px 56px 70px rgba(30, 30, 60, 0.1)',
    '0px 16px 34px rgba(30, 30, 60, 0.06), 0px 60px 75px rgba(30, 30, 60, 0.1)',
    '0px 17px 36px rgba(30, 30, 60, 0.06), 0px 64px 80px rgba(30, 30, 60, 0.1)',
    '0px 18px 38px rgba(30, 30, 60, 0.06), 0px 68px 85px rgba(30, 30, 60, 0.1)',
    '0px 19px 40px rgba(30, 30, 60, 0.06), 0px 72px 90px rgba(30, 30, 60, 0.1)',
    '0px 20px 42px rgba(30, 30, 60, 0.06), 0px 76px 95px rgba(30, 30, 60, 0.1)',
    '0px 21px 44px rgba(30, 30, 60, 0.06), 0px 80px 100px rgba(30, 30, 60, 0.1)',
    '0px 22px 46px rgba(30, 30, 60, 0.06), 0px 84px 105px rgba(30, 30, 60, 0.1)',
    '0px 23px 48px rgba(30, 30, 60, 0.06), 0px 88px 110px rgba(30, 30, 60, 0.1)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: 'smooth',
          overflowX: 'hidden',
        },
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(94, 23, 235, 0.2)',
          borderRadius: '8px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(94, 23, 235, 0.3)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          padding: '8px 16px',
          fontWeight: 500,
          '@media (max-width:600px)': {
            padding: '6px 12px',
            fontSize: '0.8rem',
          },
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 8px 16px rgba(94, 23, 235, 0.15)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        },
        contained: {
          background: 'linear-gradient(135deg, #5E17EB 0%, #8352FF 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4500D4 0%, #7241FF 100%)',
            boxShadow: '0px 8px 20px rgba(94, 23, 235, 0.25)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(94, 23, 235, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          '@media (max-width:600px)': {
            borderRadius: 12,
          },
          boxShadow: '0px 4px 20px rgba(30, 30, 60, 0.05)',
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0px 16px 30px rgba(30, 30, 60, 0.1)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '@media (max-width:600px)': {
            padding: '16px',
          },
          '&:last-child': {
            paddingBottom: '24px',
            '@media (max-width:600px)': {
              paddingBottom: '16px',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        },
        elevation1: {
          boxShadow: '0px 8px 20px rgba(30, 30, 60, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.95)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(94, 23, 235, 0.3)',
            },
            '&.Mui-focused': {
              background: 'rgba(255, 255, 255, 1)',
              boxShadow: '0px 4px 12px rgba(94, 23, 235, 0.1)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: '#5E17EB',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          '&.MuiChip-filled': {
            background: 'linear-gradient(135deg, rgba(94, 23, 235, 0.1) 0%, rgba(131, 82, 255, 0.1) 100%)',
          },
        },
        filledPrimary: {
          background: 'linear-gradient(135deg, #5E17EB 0%, #8352FF 100%)',
        },
        filledSecondary: {
          background: 'linear-gradient(135deg, #22CAAC 0%, #4DDFCA 100%)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 8px 20px rgba(30, 30, 60, 0.05)',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(30, 30, 60, 0.06)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          position: 'relative',
          overflow: 'visible',
          minWidth: 100,
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '50%',
            bottom: 0,
            width: 0,
            height: 3,
            borderRadius: 1.5,
            backgroundColor: '#5E17EB',
            transform: 'translateX(-50%)',
            transition: 'width 0.3s ease',
          },
          '&.Mui-selected::after': {
            width: '60%',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '@media (max-width:600px)': {
            fontSize: '0.9rem',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          borderRadius: 12,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0px 8px 20px rgba(30, 30, 60, 0.1)',
          borderRadius: 16,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0px 16px 40px rgba(30, 30, 60, 0.2)',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 56,
          height: 32,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(24px)',
              '& + .MuiSwitch-track': {
                opacity: 1,
                background: 'linear-gradient(135deg, #5E17EB 0%, #8352FF 100%)',
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: 28,
            height: 28,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiSwitch-track': {
            borderRadius: 16,
            opacity: 1,
            backgroundColor: '#E2E8F0',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          background: 'rgba(33, 39, 60, 0.9)',
          backdropFilter: 'blur(4px)',
          padding: '8px 16px',
          fontWeight: 500,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 600px)': {
            paddingLeft: 24,
            paddingRight: 24,
          },
          '@media (max-width:600px)': {
            padding: '0 12px',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontWeight: 500,
          color: '#5E17EB',
          transition: 'color 0.2s ease, text-decoration 0.2s ease',
          '&:hover': {
            color: '#8352FF',
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: 'rgba(94, 23, 235, 0.1)',
        },
        bar: {
          borderRadius: 8,
          background: 'linear-gradient(135deg, #5E17EB 0%, #8352FF 100%)',
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

export default theme; 